import type { ChatSearchIndex } from "@ext/agent/components/search/chatSearchIndex";
import type { ChatSearchResult } from "@ext/agent/components/search/chatSearchResults";
import { getSessions } from "@ext/agent/components/store/AgentStore";
import type { SessionStatePayload } from "@ext/agent/components/types/chat";
import { traced } from "@ext/loggers/opentelemetry";
import { useSearchableMenu } from "@ui-kit/Dropdown";
import { useCallback, useEffect, useRef, useState } from "react";

type ResolvedSearch = {
	query: string;
	results: ChatSearchResult[];
	failed: boolean;
};

const NO_RESULTS: ResolvedSearch = { query: "", results: [], failed: false };

export const useChatHistorySearch = () => {
	const { search, setSearch, contentRef, inputRef, handleContentKeyDown, handleInputKeyDown } = useSearchableMenu();
	const [index, setIndex] = useState<ChatSearchIndex | null>(null);
	const [indexFailed, setIndexFailed] = useState(false);
	const [resolved, setResolved] = useState<ResolvedSearch>(NO_RESULTS);
	const indexedSessionsRef = useRef<SessionStatePayload[] | null>(null);

	const query = search.trim();
	const isSearching = query.length > 0;

	const takeSnapshot = useCallback(() => {
		const sessions = getSessions();
		if (sessions === indexedSessionsRef.current) return;
		indexedSessionsRef.current = sessions;
		setIndex(null);
		setIndexFailed(false);
		setResolved(NO_RESULTS);
		void traced("agent-chat-search-index", { omitResult: true }, async () => {
			const { createChatSearchIndex } = await import("@ext/agent/components/search/chatSearchIndex");
			return createChatSearchIndex(sessions);
		}).then(
			(next) => {
				if (sessions === indexedSessionsRef.current) setIndex(next);
			},
			() => {
				if (sessions !== indexedSessionsRef.current) return;
				indexedSessionsRef.current = null;
				setIndexFailed(true);
			},
		);
	}, []);

	useEffect(() => {
		if (!index || !isSearching) return;
		let alive = true;
		void index.search(query).then(
			(results) => {
				if (alive) setResolved({ query, results, failed: false });
			},
			() => {
				if (alive) setResolved({ query, results: [], failed: true });
			},
		);
		return () => {
			alive = false;
		};
	}, [index, query, isSearching]);

	const isFailed = indexFailed || (resolved.query === query && resolved.failed);

	return {
		search,
		setSearch,
		contentRef,
		inputRef,
		handleContentKeyDown,
		handleInputKeyDown,
		isSearching,
		isPending: !isFailed && resolved.query !== query,
		isFailed,
		results: resolved.results,
		takeSnapshot,
	};
};
